import * as z from "zod";
import { zql } from "../generatedDrizzleSchema";
import { applyScopeGrantPatch } from "../rbac/applyScopeGrantPatch";
import { fetchGrantResources } from "../rbac/grantResources";
import { SCOPE_MANAGE_GRANTS_PERMISSION } from "../rbac/roles";
import type { GrantRemoval } from "../rbac/spaceGrantDelta";
import {
  type GrantInput,
  grantEntry,
  grantKey,
  principalKey,
} from "../rbac/validation";
import { mutator } from "./mutator";

// The roles the reviewer took off the proposal, grouped per principal. Computed
// against the *proposed* set (not the connector's live grants) so approval only
// ever revokes access this request asked for and the reviewer removed — never a
// grant someone else made to the connector between request and approval.
function lostGrantRemovals(
  proposed: readonly GrantInput[],
  edited: readonly GrantInput[]
): GrantRemoval[] {
  const kept = new Set(edited.map(grantKey));
  const lost = new Map<
    string,
    {
      userGroupId?: string;
      userId?: string;
      serviceAccountId?: string;
      roleIds: Set<string>;
    }
  >();
  for (const g of proposed) {
    if (kept.has(grantKey(g))) continue;
    const p = principalKey(g);
    const roleId = g.builtinRoleId ?? g.customRoleId;
    if (!p || !roleId) continue;
    let entry = lost.get(p);
    if (!entry) {
      entry = {
        userGroupId: g.userGroupId,
        userId: g.userId,
        serviceAccountId: g.serviceAccountId,
        roleIds: new Set(),
      };
      lost.set(p, entry);
    }
    entry.roleIds.add(roleId);
  }
  return [...lost.values()].map(({ roleIds, ...principal }) => ({
    ...principal,
    roleIds: [...roleIds],
  }));
}

export const approveGrantRequest = mutator({
  schema: z.object({
    requestId: z.string(),
    grants: z.array(grantEntry).optional(),
  }),
  resolve: {
    request: { kind: "grantRequest", from: "requestId" },
  },
  permissions: [
    {
      permission: ({ args }) =>
        SCOPE_MANAGE_GRANTS_PERMISSION[args.request.scopeKind],
      scopeId: ({ args }) => args.request.scopeId,
    },
  ],
  fn: async ({ tx, ctx, args: { request, grants: editedGrants } }) => {
    const scope = { scopeId: request.scopeId, scopeKind: request.scopeKind };
    const proposed: GrantInput[] = (
      await tx.run(zql.grantRequestGrants.where("requestId", request.id))
    ).map((g) => ({
      userGroupId: g.userGroupId ?? undefined,
      userId: g.userId ?? undefined,
      serviceAccountId: g.serviceAccountId ?? undefined,
      builtinRoleId: g.builtinRoleId ?? undefined,
      customRoleId: g.customRoleId ?? undefined,
    }));

    // A reviewer may trim, downgrade, or extend the proposal before approving.
    // Without edits, approval is add-only — it unions the proposed grants onto
    // the connector and never revokes. With edits, we also revoke the roles the
    // reviewer dropped. The reviewer's connected_app:share permission (checked
    // above) is the only gate on the resulting set; applyScopeGrantPatch adds a
    // validateGrants pass and, for service-account targets, an escalation guard.
    const desired = editedGrants ?? proposed;
    const removals = editedGrants
      ? lostGrantRemovals(proposed, editedGrants)
      : [];

    // Revoke and grant in two single-sided passes: resolveGrantDelta rejects a
    // principal appearing in both add and remove, which a downgrade would.
    if (removals.length > 0) {
      await applyScopeGrantPatch({
        tx,
        ctx,
        ...scope,
        add: [],
        remove: removals,
        customRoles: [],
      });
    }
    const resources = await fetchGrantResources(tx, desired, ctx.tenantId);
    await applyScopeGrantPatch({
      tx,
      ctx,
      ...scope,
      add: desired,
      remove: [],
      customRoles: resources.grantCustomRoles,
    });

    await tx.mutate.grantRequests.delete({ id: request.id });
  },
});
